import React, { ReactElement } from "react";
import ContainerWithBorder from "../../../components/ContainerWithBorder";
import DataRelevanceIndication from "../../../components/DataRelevanceIndication";
import GlobalSpinner from "../../../components/GlobalSpinner";
import Header from "../../../components/Header";
import { MemoizedValue } from "../../../helpers/memoizer";
import { SvgRefStore } from "../../../helpers/svg";
import { PumpStateStatuses } from "../Vis";
import AdditionalValueDisplay, { ValueWithLabelAndUnit } from "./AdditionalValueDisplay";
import Counter from "./Counter";
import Counters from "./Counters";
import ReportsAlertsHeader, { ReportsAlertsHeaderProps } from "./ReportsAlertsHeader";
import WaterLevel from "./WaterLevel";
import WorkModeSection, { WorkModeInfo } from "./WorkModeSection";
import PumpIndicator, { CurrentAlarmInfo } from "./PumpIndicator";
import MainValueBlocks, { MainValues } from "./MainValueBlocks";
import AdditionalControlForms, { AdditionalControlFormContent } from "./AdditionalControlForms";
import MaintenanceDate, { MaintenanceDateProps } from "./MaintenanceDate";

interface CounterData {
  name: string;
  value?: MemoizedValue;
}

export interface VisContentProps {
  pumpState: PumpStateStatuses;
  showSpinner: boolean;
  includeInteractiveElements: boolean;
  lastUpdateOn?: Date;
  workModesList: WorkModeInfo[];
  currentWorkModeKey?: string;
  manualBtnIsOn: boolean;
  manualSwitchPumpOnHandler?: () => Promise<void>;
  manualSwitchPumpOffHandler?: () => Promise<void>;
  waterLevel?: {
    value: number;
    max: number;
    unit: string;
  };
  mainValues: MainValues;
  currentAlarm?: CurrentAlarmInfo;
  reportsProps: ReportsAlertsHeaderProps;
  isScheduletableSetting: boolean;
  visSchedule: ReactElement;
  additionalValues: ValueWithLabelAndUnit[];
  additionalControlFormContents: AdditionalControlFormContent[];
  shortFormatForCounters: boolean;
  countersData: CounterData[];
  showReports: boolean;
  refs: SvgRefStore;
  pumpIndicationSetup: () => void;
  showMaintenanceDate: boolean;
  maintenanceDateProps: MaintenanceDateProps;
}

export default function VisContentWithChrome(
  props: VisContentProps & { name?: string },
): ReactElement {
  return (
    <ContainerWithBorder>
      {props.name && <Header name={props.name} />}
      <VisRawContent {...props} />
    </ContainerWithBorder>
  );
}

export function VisRawContent({
  pumpState,
  waterLevel,
  reportsProps,
  includeInteractiveElements,
  workModesList,
  currentWorkModeKey,
  manualBtnIsOn,
  manualSwitchPumpOnHandler,
  manualSwitchPumpOffHandler,
  isScheduletableSetting,
  visSchedule,
  additionalValues,
  currentAlarm,
  mainValues,
  lastUpdateOn,
  additionalControlFormContents,
  shortFormatForCounters,
  countersData,
  showSpinner,
  showReports,
  refs,
  pumpIndicationSetup,
  showMaintenanceDate,
  maintenanceDateProps,
}: VisContentProps): ReactElement {
  return (
    <div style={{ display: "flex", padding: "15px", position: "relative", height: "100%" }}>
      {waterLevel && <WaterLevel value={waterLevel.value} max={waterLevel.max} />}
      <div className="d-flex flex-column" style={{ width: "100%" }}>
        {includeInteractiveElements && pumpState.hasConnection && (
          <>
            {showReports && <ReportsAlertsHeader {...reportsProps} />}

            <WorkModeSection
              modesList={workModesList}
              currentModeKey={currentWorkModeKey}
              manualBtnIsOn={manualBtnIsOn}
              switchOnHandler={manualSwitchPumpOnHandler}
              switchOffHandler={manualSwitchPumpOffHandler}
              isScheduletableSetting={isScheduletableSetting}
              visSchedule={visSchedule}
            />
          </>
        )}

        {additionalValues.length > 0 && <AdditionalValueDisplay values={additionalValues} />}

        <PumpIndicator
          alarm={currentAlarm}
          pumpState={pumpState}
          refs={refs}
          pumpIndicationSetup={pumpIndicationSetup}
        />

        {showMaintenanceDate && <MaintenanceDate {...maintenanceDateProps} />}

        <MainValueBlocks {...mainValues} />

        <DataRelevanceIndication lastUpdate={lastUpdateOn} />

        {includeInteractiveElements && pumpState.hasConnection && (
          <AdditionalControlForms contents={additionalControlFormContents} />
        )}

        <Counters
          counters={countersData.map((c, i) => (
            <Counter {...c} key={i} />
          ))}
          shortFormat={shortFormatForCounters}
        />

        <GlobalSpinner isView={showSpinner} />
      </div>
    </div>
  );
}
